import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import React from 'react'
import {
    ApplyBand,
    buttonSx,
    classToFComponent,
    CollapsedPaneFilterIcon,
    CollapsedPaneHeader,
    CollapsedPaneLabel,
    FCClassContext,
    Field,
    FieldLabel,
    FilterPaneContainer,
    FilterPaneHeader,
    IconActionButton,
    inputSx,
    PaneTitle
} from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'

import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'

import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'

import EnchenteRs2024View from './tcm_enchente_rs_2024'
import FilterCatalogView from './tcm_filter_catalog'
import TcmDateKeywordFilterView from './tcm_filter_for_date'
import NumberFilterView from './tcm_filter_for_number'
import TcmStringFilterView from './tcm_filter_for_string'
import TcmStringKeywordFilterView from './tcm_filter_for_string_keyword'

import { TextField } from '@mui/material'
import { AppStorage } from 'src/utils'
import { FilterPaneScope } from '../../tcm_scopes'
import StringMultiSelectFilterView from './tcm_filter_for_string_multi_select'
import { SelectFilterView } from './tcm_filter_select'

const LOG = Logger.get('TCM-FILTER-VIEW')

const FilterBody = styled('div')({
    padding: '16px 14px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    '& > *': { flexShrink: 0 }
})

const useStyles = makeStyles()({
    expanded: {
        width: '380px'
    },
    shrinked: {
        minWidth: 36
    }
})

const appStorage = AppStorage.singleton()

type FilterPaneViewProps = {
    className?: string
    scope: FilterPaneScope
}

class FilterPaneViewClass implements FCClassContext<FilterPaneViewProps> {
    // :: Fields

    scope!: FilterPaneScope
    classes!: ReturnType<typeof useStyles>['classes']

    // :: Emissors

    readonly emitToggleExpanded = () => this.scope.onToggleExpanded().catch(LOG.caught)
    readonly emitClearFilters = () => this.scope.onClearFilters().catch(LOG.caught)
    readonly emitApply = () => this.scope.onApply().catch(LOG.caught)
    readonly emitSearchFilterChanged = (filter: string) => this.scope.onSearchFilterChanged(filter).catch(LOG.caught)

    // :: Methods

    render({ className, scope }: FilterPaneViewProps) {
        this.scope = scope
        this.classes = useStyles().classes

        return (
            <FilterPaneContainer
                className={clsx(scope.expanded ? this.classes.expanded : this.classes.shrinked, className)}
            >
                {scope.expanded ? this.renderOpenFilter() : this.renderCloseFilter()}
            </FilterPaneContainer>
        )
    }

    renderOpenFilter() {
        const { scope } = this

        const filterList: { order: number; element: React.JSX.Element }[] = []

        if (scope.nomeEmpresa.visible) {
            filterList.push({
                order: scope.nomeEmpresa.order,
                element: <TcmStringFilterView key="nomeEmpresa" scope={scope.nomeEmpresa} />
            })
        }

        if (scope.nomeFantasia.visible) {
            filterList.push({
                order: scope.nomeFantasia.order,
                element: <TcmStringFilterView key="nomeFantasia" scope={scope.nomeFantasia} />
            })
        }

        if (scope.cnpj.visible) {
            filterList.push({
                order: scope.cnpj.order,
                element: <TcmStringKeywordFilterView key="cnpj" scope={scope.cnpj} />
            })
        }

        if (scope.cnpjMatriz.visible) {
            filterList.push({
                order: scope.cnpjMatriz.order,
                element: <TcmStringKeywordFilterView key="cnpjMatriz" scope={scope.cnpjMatriz} />
            })
        }

        if (scope.nomeMatriz.visible) {
            filterList.push({
                order: scope.nomeMatriz.order,
                element: <TcmStringFilterView key="nomeMatriz" scope={scope.nomeMatriz} />
            })
        }

        if (scope.cnpjGrupo.visible) {
            filterList.push({
                order: scope.cnpjGrupo.order,
                element: <TcmStringKeywordFilterView key="cnpjGrupo" scope={scope.cnpjGrupo} />
            })
        }

        if (scope.dtInicioAtividade.visible) {
            filterList.push({
                order: scope.dtInicioAtividade.order,
                element: <TcmDateKeywordFilterView key="dtInicioAtividade" scope={scope.dtInicioAtividade} />
            })
        }

        if (scope.situacaoCadastral.visible) {
            filterList.push({
                order: scope.situacaoCadastral.order,
                element: <StringMultiSelectFilterView key="situacaoCadastral" scope={scope.situacaoCadastral} />
            })
        }

        if (scope.dtSituacaoCadastral.visible) {
            filterList.push({
                order: scope.dtSituacaoCadastral.order,
                element: <TcmDateKeywordFilterView key="dtSituacaoCadastral" scope={scope.dtSituacaoCadastral} />
            })
        }

        if (scope.dtCompetenciaInicioAtividade.visible) {
            filterList.push({
                order: scope.dtCompetenciaInicioAtividade.order,
                element: (
                    <TcmDateKeywordFilterView
                        key="dtCompetenciaInicioAtividade"
                        scope={scope.dtCompetenciaInicioAtividade}
                    />
                )
            })
        }

        if (scope.matrizFilial.visible) {
            filterList.push({
                order: scope.matrizFilial.order,
                element: <SelectFilterView key="matrizFilial" scope={scope.matrizFilial} />
            })
        }
        if (scope.regimeTributario.visible) {
            filterList.push({
                order: scope.regimeTributario.order,
                element: <StringMultiSelectFilterView key="regimeTributario" scope={scope.regimeTributario} />
            })
        }
        if (scope.porteReceitaFederal.visible) {
            filterList.push({
                order: scope.porteReceitaFederal.order,
                element: <StringMultiSelectFilterView key="porteReceitaFederal" scope={scope.porteReceitaFederal} />
            })
        }
        if (scope.regiaoGeograficaImediataCodes.visible) {
            filterList.push({
                order: scope.regiaoGeograficaImediataCodes.order,
                element: (
                    <StringMultiSelectFilterView
                        key="regiaoGeograficaImediataCodes"
                        scope={scope.regiaoGeograficaImediataCodes}
                    />
                )
            })
        }
        if (scope.regiaoGeograficaIntermediariaCodes.visible) {
            filterList.push({
                order: scope.regiaoGeograficaIntermediariaCodes.order,
                element: (
                    <StringMultiSelectFilterView
                        key="regiaoGeograficaIntermediariaCodes"
                        scope={scope.regiaoGeograficaIntermediariaCodes}
                    />
                )
            })
        }

        if (scope.portePorFuncionarios.visible) {
            filterList.push({
                order: scope.portePorFuncionarios.order,
                element: <StringMultiSelectFilterView key="portePorFuncionarios" scope={scope.portePorFuncionarios} />
            })
        }

        if (scope.portePorFaturamentoEstimadoCnpj.visible) {
            filterList.push({
                order: scope.portePorFaturamentoEstimadoCnpj.order,
                element: (
                    <StringMultiSelectFilterView
                        key="portePorFaturamentoEstimadoCnpj"
                        scope={scope.portePorFaturamentoEstimadoCnpj}
                    />
                )
            })
        }
        if (scope.portePorFaturamentoEstimadoGrupo.visible) {
            filterList.push({
                order: scope.portePorFaturamentoEstimadoGrupo.order,
                element: (
                    <StringMultiSelectFilterView
                        key="portePorFaturamentoEstimadoGrupo"
                        scope={scope.portePorFaturamentoEstimadoGrupo}
                    />
                )
            })
        }
        if (scope.cadeiaValorSaude.visible) {
            filterList.push({
                order: scope.cadeiaValorSaude.order,
                element: <SelectFilterView key="cadeiaValorSaude" scope={scope.cadeiaValorSaude} />
            })
        }

        if (scope.naturezaJuridicaGrupoCodes.visible) {
            filterList.push({
                order: scope.naturezaJuridicaGrupoCodes.order,
                element: (
                    <StringMultiSelectFilterView
                        key="naturezaJuridicaGrupoCodes"
                        scope={scope.naturezaJuridicaGrupoCodes}
                    />
                )
            })
        }
        if (scope.naturezaJuridicaPublicaPrivadaCodes.visible) {
            filterList.push({
                order: scope.naturezaJuridicaPublicaPrivadaCodes.order,
                element: (
                    <SelectFilterView
                        key="naturezaJuridicaPublicaPrivadaCodes"
                        scope={scope.naturezaJuridicaPublicaPrivadaCodes}
                    />
                )
            })
        }
        if (scope.naturezaJuridicaCodes.visible) {
            filterList.push({
                order: scope.naturezaJuridicaCodes.order,
                element: (
                    <StringMultiSelectFilterView key="natureza-juridica-codes" scope={scope.naturezaJuridicaCodes} />
                )
            })
        }

        if (scope.cnesTexts.visible) {
            filterList.push({
                order: scope.cnesTexts.order,
                element: <TcmStringKeywordFilterView key="cnes-texts" scope={scope.cnesTexts} />
            })
        }

        if (scope.cnaeTexts.visible) {
            filterList.push({
                order: scope.cnaeTexts.order,
                element: <TcmStringFilterView key="cnae-texts" scope={scope.cnaeTexts} />
            })
        }

        if (scope.tipoCnaeTexts.visible) {
            filterList.push({
                order: scope.tipoCnaeTexts.order,
                element: <StringMultiSelectFilterView key="cnae" scope={scope.tipoCnaeTexts} />
            })
        }

        if (scope.tipoEstabelecimentoSaudeCodes.visible) {
            filterList.push({
                order: scope.tipoEstabelecimentoSaudeCodes.order,
                element: (
                    <StringMultiSelectFilterView
                        key="tipoEstabelecimentoSaudeCodes"
                        scope={scope.tipoEstabelecimentoSaudeCodes}
                    />
                )
            })
        }

        if (scope.porteEstabelecimentoSaudeCodes.visible) {
            filterList.push({
                order: scope.porteEstabelecimentoSaudeCodes.order,
                element: (
                    <StringMultiSelectFilterView
                        key="porteEstabelecimentoSaudeCodes"
                        scope={scope.porteEstabelecimentoSaudeCodes}
                    />
                )
            })
        }

        if (scope.cnaeClasseCodes.visible) {
            filterList.push({
                order: scope.cnaeClasseCodes.order,
                element: <StringMultiSelectFilterView key="cnaeClasseCodes" scope={scope.cnaeClasseCodes} />
            })
        }

        if (scope.cnaeDivisaoCodes.visible) {
            filterList.push({
                order: scope.cnaeDivisaoCodes.order,
                element: <StringMultiSelectFilterView key="cnaeDivisaoCodes" scope={scope.cnaeDivisaoCodes} />
            })
        }

        if (scope.cnaeGrupoCodes.visible) {
            filterList.push({
                order: scope.cnaeGrupoCodes.order,
                element: <StringMultiSelectFilterView key="cnaeGrupoCodes" scope={scope.cnaeGrupoCodes} />
            })
        }

        if (scope.cnaeSecaoCodes.visible) {
            filterList.push({
                order: scope.cnaeSecaoCodes.order,
                element: <StringMultiSelectFilterView key="cnaeSecaoCodes" scope={scope.cnaeSecaoCodes} />
            })
        }

        if (scope.cnaeSetorCods.visible) {
            filterList.push({
                order: scope.cnaeSetorCods.order,
                element: <StringMultiSelectFilterView key="cnaeSetorCods" scope={scope.cnaeSetorCods} />
            })
        }

        if (scope.scoreRiscos.visible) {
            filterList.push({
                order: scope.scoreRiscos.order,
                element: <StringMultiSelectFilterView key="score-riscos" scope={scope.scoreRiscos} />
            })
        }

        if (scope.uf.visible) {
            filterList.push({
                order: scope.uf.order,
                element: <StringMultiSelectFilterView key="uf-codigo" scope={scope.uf} />
            })
        }

        if (scope.cidadeCodes.visible) {
            filterList.push({
                order: scope.cidadeCodes.order,
                element: <StringMultiSelectFilterView key="cidade-codes" scope={scope.cidadeCodes} />
            })
        }

        if (scope.cboCodes.visible) {
            filterList.push({
                order: scope.cboCodes.order,
                element: <StringMultiSelectFilterView key="cbo-codes" scope={scope.cboCodes} />
            })
        }

        if (scope.pesCboCodes.visible) {
            filterList.push({
                order: scope.pesCboCodes.order,
                element: <StringMultiSelectFilterView key="pes-cbo-codes" scope={scope.pesCboCodes} />
            })
        }

        if (scope.cboCategorias.visible) {
            filterList.push({
                order: scope.cboCategorias.order,
                element: <StringMultiSelectFilterView key="cbo-categorias" scope={scope.cboCategorias} />
            })
        }

        if (scope.pesCboCategorias.visible) {
            filterList.push({
                order: scope.pesCboCategorias.order,
                element: <StringMultiSelectFilterView key="pes-cbo-categorias" scope={scope.pesCboCategorias} />
            })
        }

        if (scope.bairroTexts.visible) {
            filterList.push({
                order: scope.bairroTexts.order,
                element: <TcmStringFilterView key="bairro-texts" scope={scope.bairroTexts} />
            })
        }

        if (scope.regiaoMetropolitanaCodes.visible) {
            filterList.push({
                order: scope.regiaoMetropolitanaCodes.order,
                element: <StringMultiSelectFilterView key="regiao-metro-codes" scope={scope.regiaoMetropolitanaCodes} />
            })
        }

        if (scope.totalEmpresaGrupo.visible) {
            filterList.push({
                order: scope.totalEmpresaGrupo.order,
                element: <NumberFilterView key="qtde-empresa-grupo" scope={scope.totalEmpresaGrupo} />
            })
        }

        if (scope.totalEmpresaCapSocial.visible) {
            filterList.push({
                order: scope.totalEmpresaCapSocial.order,
                element: <NumberFilterView key="qtde-empresa-cap-social" scope={scope.totalEmpresaCapSocial} />
            })
        }

        if (scope.idadeEmpresa.visible) {
            filterList.push({
                order: scope.idadeEmpresa.order,
                element: <NumberFilterView key="idade-empresa" scope={scope.idadeEmpresa} />
            })
        }

        if (scope.socioNome.visible) {
            filterList.push({
                order: scope.socioNome.order,
                element: <TcmStringFilterView key="socio-nome" scope={scope.socioNome} />
            })
        }

        if (scope.prfNome.visible) {
            filterList.push({
                order: scope.prfNome.order,
                element: <TcmStringFilterView key="medico-nome" scope={scope.prfNome} />
            })
        }

        if (scope.pesNome.visible) {
            filterList.push({
                order: scope.pesNome.order,
                element: <TcmStringFilterView key="empregado-nome" scope={scope.pesNome} />
            })
        }

        if (scope.totalFuncionarios.visible) {
            filterList.push({
                order: scope.totalFuncionarios.order,
                element: <NumberFilterView key="qtde-funcionarios" scope={scope.totalFuncionarios} />
            })
        }

        if (scope.totalFuncionarios_0_a_18.visible) {
            filterList.push({
                order: scope.totalFuncionarios_0_a_18.order,
                element: <NumberFilterView key="qtde-funcionarios-0-18" scope={scope.totalFuncionarios_0_a_18} />
            })
        }

        if (scope.totalFuncionarios_19_a_33.visible) {
            filterList.push({
                order: scope.totalFuncionarios_19_a_33.order,
                element: <NumberFilterView key="qtde-funcionarios-19-33" scope={scope.totalFuncionarios_19_a_33} />
            })
        }

        if (scope.totalFuncionarios_34_a_48.visible) {
            filterList.push({
                order: scope.totalFuncionarios_34_a_48.order,
                element: <NumberFilterView key="qtde-funcionarios-33-48" scope={scope.totalFuncionarios_34_a_48} />
            })
        }

        if (scope.totalFuncionarios_49_a_58.visible) {
            filterList.push({
                order: scope.totalFuncionarios_49_a_58.order,
                element: <NumberFilterView key="qtde-funcionarios-49-58" scope={scope.totalFuncionarios_49_a_58} />
            })
        }

        if (scope.totalFuncionarios_59_ou_mais.visible) {
            filterList.push({
                order: scope.totalFuncionarios_59_ou_mais.order,
                element: (
                    <NumberFilterView key="qtde-funcionarios-59-ou-mais" scope={scope.totalFuncionarios_59_ou_mais} />
                )
            })
        }

        if (scope.totalFuncionariosFemininos.visible) {
            filterList.push({
                order: scope.totalFuncionariosFemininos.order,
                element: <NumberFilterView key="qtde-funcionarios-fem" scope={scope.totalFuncionariosFemininos} />
            })
        }

        if (scope.totalFuncionariosMasculinos.visible) {
            filterList.push({
                order: scope.totalFuncionariosMasculinos.order,
                element: <NumberFilterView key="qtde-funcionarios-masc" scope={scope.totalFuncionariosMasculinos} />
            })
        }

        if (scope.totalFuncionariosDoGrupo.visible) {
            filterList.push({
                order: scope.totalFuncionariosDoGrupo.order,
                element: <NumberFilterView key="qtde-funcionarios-grupo" scope={scope.totalFuncionariosDoGrupo} />
            })
        }

        if (scope.faixaFaturamentoAnual.visible) {
            filterList.push({
                order: scope.faixaFaturamentoAnual.order,
                element: <NumberFilterView key="faixa-fat-anual" scope={scope.faixaFaturamentoAnual} />
            })
        }

        if (scope.faixaFaturamentoConsolidadoGrupo.visible) {
            filterList.push({
                order: scope.faixaFaturamentoConsolidadoGrupo.order,
                element: <NumberFilterView key="faixa-fat-cons-grp" scope={scope.faixaFaturamentoConsolidadoGrupo} />
            })
        }

        if (scope.matrizUfs.visible) {
            filterList.push({
                order: scope.matrizUfs.order,
                element: <StringMultiSelectFilterView key="matrizUfs" scope={scope.matrizUfs} />
            })
        }

        if (scope.cep.visible) {
            filterList.push({
                order: scope.cep.order,
                element: <TcmStringKeywordFilterView key="cep" scope={scope.cep} />
            })
        }

        if (scope.logradouro.visible) {
            filterList.push({
                order: scope.logradouro.order,
                element: <TcmStringFilterView key="logradouro" scope={scope.logradouro} />
            })
        }

        if (scope.enderecoNumeros.visible) {
            filterList.push({
                order: scope.enderecoNumeros.order,
                element: <TcmStringKeywordFilterView key="enderecoNumeros" scope={scope.enderecoNumeros} />
            })
        }

        if (scope.enderecoComplementoTexts.visible) {
            filterList.push({
                order: scope.enderecoComplementoTexts.order,
                element: <TcmStringFilterView key="enderecoComplementoTexts" scope={scope.enderecoComplementoTexts} />
            })
        }

        if (scope.enderecoLatitude.visible) {
            filterList.push({
                order: scope.enderecoLatitude.order,
                element: <NumberFilterView key="enderecoLatitude" scope={scope.enderecoLatitude} />
            })
        }

        if (scope.enderecoLongitude.visible) {
            filterList.push({
                order: scope.enderecoLongitude.order,
                element: <NumberFilterView key="enderecoLongitude" scope={scope.enderecoLongitude} />
            })
        }

        if (scope.telefone1.visible) {
            filterList.push({
                order: scope.telefone1.order,
                element: <TcmStringFilterView key="telefone1" scope={scope.telefone1} />
            })
        }

        if (scope.telefone2.visible) {
            filterList.push({
                order: scope.telefone2.order,
                element: <TcmStringFilterView key="telefone2" scope={scope.telefone2} />
            })
        }

        if (scope.email.visible) {
            filterList.push({
                order: scope.email.order,
                element: <TcmStringFilterView key="email" scope={scope.email} />
            })
        }

        if (scope.totalFiliais.visible) {
            filterList.push({
                order: scope.totalFiliais.order,
                element: <NumberFilterView key="totalFiliais" scope={scope.totalFiliais} />
            })
        }

        if (scope.totalFuncionariosMatrizEFiliais.visible) {
            filterList.push({
                order: scope.totalFuncionariosMatrizEFiliais.order,
                element: (
                    <NumberFilterView
                        key="totalFuncionariosMatrizEFiliais"
                        scope={scope.totalFuncionariosMatrizEFiliais}
                    />
                )
            })
        }

        if (scope.salarioTotal.visible) {
            filterList.push({
                order: scope.salarioTotal.order,
                element: <NumberFilterView key="salarioTotal" scope={scope.salarioTotal} />
            })
        }

        if (scope.salarioTotalGrupo.visible) {
            filterList.push({
                order: scope.salarioTotalGrupo.order,
                element: <NumberFilterView key="salarioTotalGrupo" scope={scope.salarioTotalGrupo} />
            })
        }

        if (scope.optanteSimples.visible) {
            filterList.push({
                order: scope.optanteSimples.order,
                element: <StringMultiSelectFilterView key="optanteSimples" scope={scope.optanteSimples} />
            })
        }

        if (scope.optanteMei.visible) {
            filterList.push({
                order: scope.optanteMei.order,
                element: <StringMultiSelectFilterView key="optanteMei" scope={scope.optanteMei} />
            })
        }

        if (scope.webNomeTexts.visible) {
            filterList.push({
                order: scope.webNomeTexts.order,
                element: <TcmStringFilterView key="webNomeTexts" scope={scope.webNomeTexts} />
            })
        }

        if (scope.webCategoriasTexts.visible) {
            filterList.push({
                order: scope.webCategoriasTexts.order,
                element: <TcmStringFilterView key="webCategoriasTexts" scope={scope.webCategoriasTexts} />
            })
        }

        if (scope.webNota.visible) {
            filterList.push({
                order: scope.webNota.order,
                element: <NumberFilterView key="webNota" scope={scope.webNota} />
            })
        }

        if (scope.webQtdAvaliacoes.visible) {
            filterList.push({
                order: scope.webQtdAvaliacoes.order,
                element: <NumberFilterView key="webQtdAvaliacoes" scope={scope.webQtdAvaliacoes} />
            })
        }

        if (scope.webTempoMedio.visible) {
            filterList.push({
                order: scope.webTempoMedio.order,
                element: <NumberFilterView key="webTempoMedio" scope={scope.webTempoMedio} />
            })
        }

        if (scope.webSite.visible) {
            filterList.push({
                order: scope.webSite.order,
                element: <TcmStringFilterView key="webSite" scope={scope.webSite} />
            })
        }

        if (scope.webTelefone.visible) {
            filterList.push({
                order: scope.webTelefone.order,
                element: <TcmStringFilterView key="webTelefone" scope={scope.webTelefone} />
            })
        }

        filterList.sort((a, b) => a.order - b.order)

        return (
            <>
                <FilterPaneHeader>
                    <FilterListIcon />
                    <PaneTitle>Filtros</PaneTitle>
                    <Tooltip title="Ocultar filtros">
                        <IconActionButton size="small" aria-label="Ocultar filtros" onClick={this.emitToggleExpanded}>
                            <KeyboardDoubleArrowRightIcon />
                        </IconActionButton>
                    </Tooltip>
                </FilterPaneHeader>
                <FilterBody>
                    <FilterCatalogView scope={scope.catalog} />

                    {appStorage.tcmEnchenteRs2024Enabled && <EnchenteRs2024View scope={scope.enchenteRs2024} />}

                    <Field>
                        <FieldLabel htmlFor="tcmFilterSearch">Pesquisar filtros</FieldLabel>
                        <TextField
                            id="tcmFilterSearch"
                            value={scope.searchFilterText}
                            onChange={(e) => this.emitSearchFilterChanged(e.target.value)}
                            placeholder="Buscar por nome do filtro"
                            size="small"
                            sx={inputSx}
                            fullWidth
                        />
                    </Field>
                    {filterList.map((e) => e.element)}
                </FilterBody>
                <ApplyBand>
                    <Button
                        sx={buttonSx()}
                        onClick={this.emitClearFilters}
                        disabled={!scope.isFiltering}
                        startIcon={<CleaningServicesIcon />}
                    >
                        Limpar filtros
                    </Button>
                    <Button sx={buttonSx('primary')} onClick={this.emitApply}>
                        Aplicar
                    </Button>
                </ApplyBand>
            </>
        )
    }

    renderCloseFilter() {
        return (
            <CollapsedPaneHeader>
                <Tooltip title="Mostrar filtros">
                    <IconActionButton size="small" aria-label="Mostrar filtros" onClick={this.emitToggleExpanded}>
                        <KeyboardDoubleArrowLeftIcon />
                    </IconActionButton>
                </Tooltip>
                {this.scope.isFiltering && (
                    <Tooltip title="Limpar filtros">
                        <IconActionButton size="small" aria-label="Limpar filtros" onClick={this.emitClearFilters}>
                            <CleaningServicesIcon />
                        </IconActionButton>
                    </Tooltip>
                )}
                <CollapsedPaneFilterIcon />
                <CollapsedPaneLabel>Filtros</CollapsedPaneLabel>
            </CollapsedPaneHeader>
        )
    }
}
export const FilterPaneView = classToFComponent(FilterPaneViewClass, React)
export default FilterPaneView

// :: Helpers
